/* Kaguyasu persistent product actions v1.0.2. Reuses Ella's product form. */
(() => {
  if (customElements.get('kagu-product-actions')) return;

  class KaguProductActions extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.form = document.getElementById(this.dataset.formId);
      this.info = this.form?.closest('product-info');
      if (!this.form || !this.info) return;
      this.initial = JSON.parse(this.querySelector('[data-kagu-initial]').textContent);
      this.strings = JSON.parse(this.querySelector('[data-kagu-strings]').textContent);
      this.price = this.querySelector('[data-kagu-price]').textContent.trim();
      this.variant = this.initial.variant;
      this.optionsDialog = this.querySelector('dialog[id$="-options"]');
      this.contactDialog = this.querySelector('dialog[id$="-contact"]');
      this.bar = this.querySelector('.kagu-pa__bar');
      const signal = this.abort.signal;

      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.querySelector('[data-kagu-option-fields]').addEventListener('change', (event) => this.changeOption(event), { signal });
      this.querySelector('[data-kagu-quantity]').addEventListener('change', (event) => this.changeQuantity(event), { signal });
      this.form.addEventListener('change', () => this.sync(), { signal });
      this.info.addEventListener('input', () => this.queueSync(), { signal });
      this.info.addEventListener('kagu:variant-start', () => {
        this.resolving = true;
        this.selectionFailed = false;
        this.message = '';
        this.sync();
      }, { signal });
      this.info.addEventListener('kagu:variant-end', (event) => {
        this.resolving = false;
        this.selectionFailed = !event.detail.success;
        this.sync();
        if (this.optionsDialog.open) this.buildOptions();
      }, { signal });
      this.form.addEventListener('kagu:cart-start', (event) => {
        this.cartBusy = true;
        this.intent = event.detail.intent;
        this.message = '';
        this.sync();
      }, { signal });
      this.form.addEventListener('kagu:cart-end', (event) => {
        this.cartBusy = false;
        this.message = event.detail.success ? this.strings.added : event.detail.message || this.strings.error;
        this.sync();
      }, { signal });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data.sectionId) !== this.dataset.sectionId) return;
          this.variant = data.variant;
          const fresh = data.html?.getElementById(this.id)?.querySelector('[data-kagu-price]');
          if (fresh) this.price = fresh.textContent.trim();
          this.sync();
        });
      }

      // Observe only the authoritative buy form, not the whole document.
      this.observer = new MutationObserver(() => this.queueSync());
      this.observer.observe(this.form, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'min', 'max', 'step', 'value'] });
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.bar);
      for (const dialog of [this.optionsDialog, this.contactDialog]) {
        dialog.addEventListener('click', (event) => {
          if (event.target !== dialog) return;
          const box = dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
        }, { signal });
        dialog.addEventListener('close', () => {
          document.documentElement.classList.remove('kagu-pa-modal');
          this.returnFocus?.focus({ preventScroll: true });
        }, { signal });
      }

      document.body.classList.add('kagu-pa-page');
      document.body.classList.toggle('kagu-pa-hide-toolbar', this.dataset.hideToolbar !== 'false');
      document.body.classList.toggle('kagu-pa-keep-toolbar', this.dataset.hideToolbar === 'false');
      document.body.classList.toggle('kagu-pa-widgets', this.dataset.moveWidgets !== 'false');
      this.hidden = false;
      this.sync();
      this.measure();
      if (this.dataset.moveWidgets !== 'false') this.connectChat();
      if (this.querySelector('[data-kagu-contact-result]') && location.hash === '#kagu-contact-result') {
        this.openDialog(this.contactDialog);
      }
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.chatDiscovery?.disconnect();
      this.chatObserver?.disconnect();
      this.chatResizeObserver?.disconnect();
      this.chatRoot = null;
      this.chatLauncher = null;
      this.unsubscribeVariant?.();
      cancelAnimationFrame(this.frame);
      this.optionsDialog?.close();
      this.contactDialog?.close();
      // A theme-editor section replacement may already contain a new bar.
      queueMicrotask(() => {
        if (!document.querySelector('kagu-product-actions:not([hidden])')) {
          document.body.classList.remove('kagu-pa-page', 'kagu-pa-hide-toolbar', 'kagu-pa-keep-toolbar', 'kagu-pa-widgets', 'kagu-pa-chat-open', 'kagu-pa-chat-preview');
          document.body.style.removeProperty('--kagu-pa-height');
          document.body.style.removeProperty('--kagu-pa-chat-space');
          document.body.style.removeProperty('--kagu-pa-chat-height');
          document.documentElement.classList.remove('kagu-pa-modal');
        }
      });
    }

    get picker() {
      return this.info.querySelector('variant-selects[data-context^="main"]') || this.info.querySelector('variant-selects:not([data-context^="sticky"])');
    }
    get quantity() {
      return this.form.querySelector('[name="quantity"]') || this.info.querySelector('input[name="quantity"][form="' + this.form.id + '"]');
    }
    get mainButton() { return this.form.querySelector('.add-to-cart-button'); }

    queueSync() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.sync());
    }
    measure() {
      if (this.isConnected && !this.hidden) document.body.style.setProperty('--kagu-pa-height', Math.ceil(this.bar.getBoundingClientRect().height) + 'px');
    }

    connectChat() {
      // Willdesk loads asynchronously. Discover only its verified root; never
      // search every iframe or floating button, or change the vendor's styles.
      const findRoot = () => {
        const root = document.getElementById('__helpdesk-channel');
        if (root === this.chatRoot) return;
        this.chatObserver?.disconnect();
        this.chatResizeObserver?.disconnect();
        this.chatRoot = root;
        this.chatLauncher = null;
        if (root) {
          this.chatObserver = new MutationObserver(() => this.syncChat());
          this.chatObserver.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
        }
        this.syncChat();
      };
      this.chatResizeObserver = new ResizeObserver(() => this.syncChat());
      this.chatDiscovery = new MutationObserver((records) => {
        if (this.chatRoot && !this.chatRoot.isConnected) { findRoot(); return; }
        if (this.chatRoot) return;
        for (const record of records) {
          if ([...record.addedNodes].some((node) => node.nodeType === 1 &&
            (node.id === '__helpdesk-channel' || node.querySelector('#__helpdesk-channel')))) {
            findRoot();
            break;
          }
        }
      });
      this.chatDiscovery.observe(document.body, { childList: true, subtree: true });
      window.addEventListener('resize', () => this.syncChat(), { signal: this.abort.signal });
      window.visualViewport?.addEventListener('resize', () => this.syncChat(), { signal: this.abort.signal });
      findRoot();
      this.syncChat();
    }

    syncChat() {
      if (!this.isConnected || this.hidden) return;
      const root = this.chatRoot;
      const launcher = root?.querySelector('.helpdesk-channel-btn-show, .helpdesk-channel-btn-hidden');
      if (launcher !== this.chatLauncher) {
        this.chatResizeObserver?.disconnect();
        this.chatLauncher = launcher;
        if (launcher) this.chatResizeObserver?.observe(launcher);
      }
      const panel = root?.querySelector('.__iframe-container-helpdesk-channel');
      // On desktop Willdesk retains main-visible even after closing. The
      // launcher switches show/hidden synchronously on both desktop/mobile.
      const open = !!(panel?.classList.contains('helpdesk-channel-main-visible') &&
        launcher?.classList.contains('helpdesk-channel-btn-hidden'));
      document.body.classList.toggle('kagu-pa-chat-open', open);
      if (open) {
        // A vendor-triggered conversation may open while our sheet is up.
        // Native modal dialogs are above every z-index, so yield the top layer.
        this.returnFocus = null;
        for (const dialog of [this.optionsDialog, this.contactDialog]) {
          if (dialog.open) dialog.close();
        }
      }
      const style = launcher && getComputedStyle(launcher);
      const height = !open && launcher?.classList.contains('helpdesk-channel-btn-show') &&
        !launcher.hidden && style.display !== 'none' && style.visibility !== 'hidden'
        ? Math.ceil(launcher.getBoundingClientRect().height) : 0;
      for (const [name, value] of [
        ['--kagu-pa-chat-height', Math.max(0, height) + 'px'],
        ['--kagu-pa-chat-space', (height > 0 ? height + 12 : 0) + 'px']
      ]) {
        if (document.body.style.getPropertyValue(name) !== value) document.body.style.setProperty(name, value);
      }
      // Incoming-message previews belong to chat too; don't put Ella's arrow
      // through them. Relative Willdesk post panels follow their own launcher.
      const preview = root?.querySelector('.helpdesk-channel-message-remind, .helpdesk-channel-post-show');
      const previewStyle = preview && getComputedStyle(preview);
      document.body.classList.toggle('kagu-pa-chat-preview', !!(preview && !preview.hidden &&
        previewStyle.display !== 'none' && previewStyle.visibility !== 'hidden' && previewStyle.opacity !== '0'));
    }

    sync() {
      const selected = this.picker?.querySelector('[data-selected-variant]');
      if (selected && !this.resolving) {
        try { this.variant = JSON.parse(selected.textContent); } catch (_) { this.variant = null; }
      }
      const id = this.form.querySelector('input[name="id"]')?.value;
      const variantMatches = this.variant && String(this.variant.id) === id;
      const available = !!(variantMatches && this.variant.available);
      const nativeBusy = this.mainButton?.getAttribute('aria-disabled') === 'true';
      const disabled = !available || this.mainButton?.disabled || this.resolving || this.selectionFailed || this.cartBusy || nativeBusy;
      const qty = this.quantity?.value || this.variant?.quantity_rule?.min || 1;
      const title = this.initial.single ? '' : this.variant?.title || this.strings.options_title;
      const summary = [title, this.strings.quantity + ': ' + qty].filter(Boolean).join(' · ');
      const thumbnail = this.querySelector('.kagu-pa__image');
      const imageSource = this.variant?.featured_image?.src || this.variant?.featured_media?.preview_image?.src;
      if (thumbnail && imageSource && thumbnail.dataset.variantId !== String(this.variant.id)) {
        thumbnail.src = imageSource + (imageSource.includes('?') ? '&' : '?') + 'width=112';
        thumbnail.removeAttribute('srcset');
        thumbnail.dataset.variantId = this.variant.id;
      }
      this.querySelector('[data-kagu-summary]').textContent = summary;
      this.querySelector('[data-kagu-summary]').title = summary;
      this.querySelectorAll('[data-kagu-price]').forEach((el) => {
        // Use the exact Shopify-rendered presentment price, never a fixed currency.
        el.textContent = variantMatches ? this.price : '—';
      });
      this.querySelectorAll('[data-kagu-intent]').forEach((button) => {
        button.disabled = !!disabled;
        button.setAttribute('aria-busy', String(!!this.cartBusy));
        button.textContent = !available ? (this.variant ? this.strings.sold_out : this.strings.unavailable)
          : this.cartBusy ? (this.intent === 'checkout' ? this.strings.checkout : this.strings.adding)
          : button.dataset.kaguIntent === 'checkout' ? this.strings.buy : this.strings.add;
      });
      const notice = this.resolving ? this.strings.updating : this.selectionFailed ? this.strings.selection_error
        : this.message || (!available ? this.strings.stock_notice : '');
      for (const el of this.querySelectorAll('[data-kagu-status], [data-kagu-options-status]')) {
        el.textContent = notice;
        el.hidden = !notice;
      }
      this.querySelector('[data-kagu-retry]').hidden = !this.selectionFailed;
      this.querySelectorAll('[data-kagu-option-fields] select').forEach((el) => { el.disabled = !!this.resolving; });
      this.querySelector('[data-kagu-quantity]').disabled = !!(this.resolving || this.cartBusy || !this.quantity || this.quantity.disabled);
      this.updateContact(summary, qty);
    }

    updateContact(summary, qty) {
      this.querySelector('[data-kagu-contact-summary]').textContent = summary;
      const url = new URL(this.dataset.productUrl, location.href);
      if (this.variant?.id) url.searchParams.set('variant', this.variant.id);
      for (const [selector, value] of [
        ['[data-kagu-contact-variant]', this.variant?.title || this.strings.unavailable],
        ['[data-kagu-contact-sku]', this.variant?.sku || ''],
        ['[data-kagu-contact-link]', url.href],
        ['[data-kagu-contact-quantity]', qty]
      ]) {
        const input = this.querySelector(selector);
        if (input) input.value = value;
      }
      const returnTo = this.querySelector('.kagu-pa__contact-form [name="return_to"]');
      if (returnTo) returnTo.value = url.pathname + url.search + '#kagu-contact-result';
    }

    onClick(event) {
      const target = event.target.closest('button');
      if (!target || !this.contains(target)) return;
      if (target.hasAttribute('data-kagu-close')) {
        target.closest('dialog')?.close();
      } else if (target.hasAttribute('data-kagu-edit')) {
        if (!this.picker && !this.initial.single) { this.focusMain(); return; }
        this.buildOptions();
        if (!this.initial.single && !this.groups.length) { this.focusMain(); return; }
        this.openDialog(this.optionsDialog, target);
      } else if (target.hasAttribute('data-kagu-contact')) {
        this.sync();
        if (this.dataset.contactUrl) { location.assign(this.dataset.contactUrl); return; }
        this.openDialog(this.contactDialog, target);
      } else if (target.hasAttribute('data-kagu-retry')) {
        this.lastOptionControl?.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (target.dataset.kaguIntent) {
        event.preventDefault();
        if (target.disabled || this.cartBusy || this.resolving) return;
        if (!this.validate()) return;
        this.optionsDialog.close();
        // requestSubmit preserves form fields, selling plans and Shopify's cart handler.
        this.form.requestSubmit(target);
      }
    }

    openDialog(dialog, opener) {
      if (typeof dialog.showModal !== 'function') { this.focusMain(); return; }
      this.returnFocus = opener || this.querySelector('[data-kagu-contact]');
      document.documentElement.classList.add('kagu-pa-modal');
      dialog.showModal();
    }

    buildOptions() {
      const host = this.querySelector('[data-kagu-option-fields]');
      const focusedGroup = document.activeElement?.dataset.kaguGroup;
      host.replaceChildren();
      this.groups = Array.from(this.picker?.querySelectorAll('fieldset.product-form__input, .product-form__input--dropdown') || []);
      this.groups.forEach((group, index) => {
        const wrapper = document.createElement('div');
        wrapper.className = 'kagu-pa__field';
        const label = document.createElement('label');
        const nativeLabel = group.querySelector('legend, label.form__label');
        const labelCopy = nativeLabel?.cloneNode(true);
        labelCopy?.querySelectorAll('[data-selected-value]').forEach((el) => el.remove());
        label.textContent = labelCopy?.textContent.trim().replace(/[:：]\s*$/, '') || this.strings.options_title;
        const select = document.createElement('select');
        select.id = this.id + '-option-' + index;
        select.dataset.kaguGroup = index;
        label.htmlFor = select.id;
        const nativeSelect = group.querySelector('select');
        const controls = nativeSelect ? Array.from(nativeSelect.options) : Array.from(group.querySelectorAll('input[type="radio"]'));
        controls.forEach((control) => {
          const option = document.createElement('option');
          option.value = control.value;
          option.textContent = nativeSelect ? control.textContent : control.value;
          // Visual unavailability is not a disabled attribute: dependent options
          // must remain selectable so a different valid combination can be reached.
          option.disabled = control.disabled;
          option.selected = nativeSelect ? control.selected : control.checked;
          select.append(option);
        });
        select.disabled = !!this.resolving;
        wrapper.append(label, select);
        host.append(wrapper);
      });
      const quantity = this.querySelector('[data-kagu-quantity]');
      const original = this.quantity;
      for (const attr of ['min', 'max', 'step']) {
        const value = original?.getAttribute(attr);
        if (value) quantity.setAttribute(attr, value);
        else if (attr === 'max') quantity.removeAttribute(attr);
        else quantity.setAttribute(attr, '1');
      }
      const inventory = Number(original?.dataset.inventoryQuantity);
      if (original?.dataset.inventoryPolicy === 'deny' && inventory > 0) {
        quantity.max = quantity.max ? Math.min(Number(quantity.max), inventory) : inventory;
      }
      quantity.value = original?.value || '1';
      quantity.closest('.kagu-pa__quantity-row').hidden = !original;
      if (focusedGroup !== undefined) host.querySelector('[data-kagu-group="' + focusedGroup + '"]')?.focus({ preventScroll: true });
    }

    changeOption(event) {
      const select = event.target;
      const group = this.groups?.[Number(select.dataset.kaguGroup)];
      if (!group || this.resolving) return;
      let control = group.querySelector('select');
      if (control) control.value = select.value;
      else {
        control = Array.from(group.querySelectorAll('input[type="radio"]')).find((el) => el.value === select.value);
        if (!control) return;
        control.checked = true;
      }
      this.lastOptionControl = control;
      this.info.includeVariantInUrl = true;
      control.dispatchEvent(new Event('change', { bubbles: true }));
    }

    changeQuantity(event) {
      if (!event.target.reportValidity()) return;
      if (this.quantity) {
        this.quantity.value = event.target.value;
        this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
        event.target.value = this.quantity.value;
      }
      this.sync();
    }

    validate() {
      if (this.optionsDialog.open) {
        const quantity = this.querySelector('[data-kagu-quantity]');
        if (!quantity.reportValidity()) return false;
        if (this.quantity) {
          this.quantity.value = quantity.value;
          this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      const fields = new Set([...this.form.elements, ...this.info.querySelectorAll('[name^="properties["]')]);
      for (const field of fields) {
        if (field.disabled || (field.form && field.form !== this.form) || typeof field.checkValidity !== 'function') continue;
        const uncheckedTerms = field.id?.startsWith('agree_condition-') && !field.checked;
        if (!field.checkValidity() || uncheckedTerms) {
          this.optionsDialog.close();
          this.message = this.strings.required;
          this.sync();
          field.scrollIntoView({ block: 'center', behavior: 'auto' });
          field.focus({ preventScroll: true });
          field.reportValidity();
          return false;
        }
      }
      return true;
    }

    focusMain() {
      const target = this.picker || this.info;
      target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      target.querySelector('select, input, button')?.focus({ preventScroll: true });
    }
  }
  customElements.define('kagu-product-actions', KaguProductActions);
})();
